import { computed, type EnvironmentProviders, Injectable, inject, type Provider, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { type RetouchMode } from '@lite-workspace/dto-fotoclass-book';
import { isRetouchFailReason, toRetouchFailedBy } from '@lite-workspace/shared/dto-retouch';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { type LightboxItem, toLightboxItem } from '@lite-workspace/shared/photo-lightbox';
import {
  RETOUCH_COPY,
  RETOUCH_LIGHTBOX_PORT,
  RetouchApi,
  type RetouchLightboxPhoto,
  type RetouchLightboxPort,
  retouchOriginalSrc,
} from '@lite-workspace/shared/retouch/host';
import { RETOUCH_EVENTS, type RetouchEventsSource, type RetouchSocketEvent } from '@lite-workspace/shared/retouch/ports';
import { ProjectsFacade } from '../features/projects/domain/application/projects.facade';
import type { Photo } from '../features/projects/domain/entities/project.models';
import { RetouchAccessService } from './retouch-access.service';
import { RetouchActivityService } from './retouch-activity.service';
import {
  type RetouchFileDoneData,
  type RetouchFileFailedData,
  type RetouchFileQueuedData,
  type RetouchFileStartedData,
  type RetouchFinishedData,
  type RetouchLayersProgressData,
  SocketEvent,
  SocketRoom,
  type SocketRoomEvent,
  SocketService,
} from './socket.service';

export function toRetouchEvent(event: SocketRoomEvent): RetouchSocketEvent | null {
  switch (event.eventType) {
    case SocketEvent.RETOUCH_FILE_QUEUED: {
      const { processId, fileId } = event.data as RetouchFileQueuedData;
      return { type: 'RETOUCH_FILE_QUEUED', processId, fileId };
    }

    case SocketEvent.RETOUCH_FILE_STARTED: {
      const { processId, fileId } = event.data as RetouchFileStartedData;
      return { type: 'RETOUCH_FILE_STARTED', processId, fileId };
    }

    case SocketEvent.RETOUCH_FILE_DONE: {
      const { processId, fileId, newFileId, skipped } = event.data as RetouchFileDoneData;
      return { type: 'RETOUCH_FILE_DONE', processId, fileId, newFileId, skipped: Array.isArray(skipped) ? skipped : [] };
    }

    case SocketEvent.RETOUCH_FILE_FAILED: {
      const { processId, fileId, reason } = event.data as RetouchFileFailedData;
      return isRetouchFailReason(reason)
        ? { type: 'RETOUCH_FILE_FAILED', processId, fileId, reason }
        : { type: 'RETOUCH_FILE_FAILED', processId, fileId };
    }

    case SocketEvent.RETOUCH_FINISHED: {
      const { processId, total, failed, failedBy } = event.data as RetouchFinishedData;
      const counts = toRetouchFailedBy(failedBy);
      return Object.keys(counts).length
        ? { type: 'RETOUCH_FINISHED', processId, total, failed, failedBy: counts }
        : { type: 'RETOUCH_FINISHED', processId, total, failed };
    }

    case SocketEvent.RETOUCH_LAYERS_PROGRESS: {
      const { ready, total } = event.data as RetouchLayersProgressData;
      return { type: 'RETOUCH_LAYERS_PROGRESS', ready, total };
    }

    default:
      return null;
  }
}

/** The project's FILE room narrowed to retouch events; the room is left when the last subscriber goes. */
export function retouchEventsFrom(socket: SocketService): RetouchEventsSource {
  return projectId =>
    new Observable<RetouchSocketEvent>(subscriber => {
      socket.connect();
      const subscription = socket.joinRoom(SocketRoom.FILE, projectId).subscribe({
        next: event => {
          const mapped = toRetouchEvent(event);
          if (mapped) subscriber.next(mapped);
        },
        complete: () => subscriber.complete(),
      });
      return () => {
        subscription.unsubscribe();
        socket.leaveRoom(SocketRoom.FILE, projectId);
      };
    });
}

export function provideRetouchRoot(): (Provider | EnvironmentProviders)[] {
  return [{ provide: RETOUCH_EVENTS, useFactory: () => retouchEventsFrom(inject(SocketService)) }];
}

export const retouchOriginalUrl = (projectId: string, fileId: string): string =>
  `/api/projects/${projectId}/files/${fileId}/retouch/original`;

/** Batch revert settled on RETOUCH_FINISHED; rejects when the server refused or any file failed. */
export async function revertAndSettle(
  api: RetouchApi,
  activity: RetouchActivityService,
  projectId: string,
  fileIds: readonly string[],
): Promise<void> {
  const processIds = await api.revertMany(projectId, fileIds);
  const outcomes = await Promise.all(processIds.map(id => activity.processOutcome(projectId, id)));
  if (outcomes.includes('failed')) throw new Error('retouch revert failed');
}

const ADD_FAILED = 'Не удалось добавить — попробуйте ещё раз';

/** «В ретушь» / «Убрать из ретуши» of the cabinet with their toasts: the «Фото» bar and the lightbox act share it. */
@Injectable({ providedIn: 'root' })
export class FotoclassRetouchQueue implements RetouchLightboxPort {
  private readonly router = inject(Router);
  private readonly facade = inject(ProjectsFacade);
  private readonly activity = inject(RetouchActivityService);
  private readonly toast = inject(ToastService);
  private readonly retouch = inject(RetouchApi);
  private readonly access = inject(RetouchAccessService);

  private readonly scope = computed(() => new Map(this.facade.retouchScope().map(photo => [photo.id, photo])));
  /** A page's own demo read: a direct «Фото» load never reads the detail `queueActs` judges by. */
  private readonly demoRead = signal<{ projectId: string; demo: boolean } | null>(null);

  /** The lightbox acts by the project's mode; a demo project has no queue act, as on the grid. */
  readonly mode = computed<RetouchMode>(() => {
    const read = this.demoRead();
    const demo = !!read?.demo && read.projectId === this.facade.currentProjectId();
    return this.access.queueActs() && !demo ? this.access.mode() : 'off';
  });

  /** The photo the lightbox shows as its original («Исходник»), one at a time. */
  private readonly shownOriginal = signal<string | null>(null);
  private readonly shownOriginalSrc = retouchOriginalSrc(() => {
    const id = this.shownOriginal();
    return id ? this.originalUrl(id) : null;
  });

  photo(fileId: string): RetouchLightboxPhoto {
    const inScope = this.scope().get(fileId);
    // The feed's tags too: a `retouched` photo outside the scope is never offered «В ретушь».
    const photo: Pick<Photo, 'id'> & Partial<Photo> = inScope ??
      this.facade.photos().find(row => row.id === fileId) ?? { id: fileId };
    return {
      status: this.activity.markOf(photo),
      waiting: !!inScope && !this.activity.isDone(inScope),
      hasBackup: photo.hasBackup === true,
    };
  }

  /** `toLightboxItem` of a page's lightbox, the original put on screen for the photo whose «Исходник» is on. */
  readonly lightboxItem = (photo: Photo): LightboxItem => {
    const item = toLightboxItem(photo);
    const url = photo.id === this.shownOriginal() ? this.shownOriginalSrc() : null;
    return url ? { ...item, src: url, fullSrc: url } : item;
  };

  noteDemo(projectId: string, demo: boolean): void {
    this.demoRead.set({ projectId, demo });
  }

  /** «Применить AI-ретушь»: the single-photo editor; its «Назад» reopens this photo on «Фото». */
  apply(fileId: string): void {
    const projectId = this.facade.currentProjectId();
    if (projectId) this.activity.openSingle(projectId, fileId, `/projects/${projectId}/photos?photo=${fileId}`);
  }

  add(fileId: string): void {
    void this.queue([fileId]);
  }

  remove(fileId: string): void {
    void this.unqueue([fileId]);
  }

  /** After the pill's confirm: the photo goes back to «Ждут ретуши» once the lists re-read. */
  revert(fileId: string): void {
    const projectId = this.facade.currentProjectId();
    if (!projectId) return;
    revertAndSettle(this.retouch, this.activity, projectId, [fileId]).then(
      () => {
        if (this.shownOriginal() === fileId) this.shownOriginal.set(null);
        this.activity.refresh(projectId);
      },
      () => this.toast.error(RETOUCH_COPY.revert.failed),
    );
  }

  originalUrl(fileId: string): string {
    return retouchOriginalUrl(this.facade.currentProjectId() ?? '', fileId);
  }

  showOriginal(fileId: string, on: boolean): void {
    this.shownOriginal.set(on ? fileId : null);
  }

  /** Tags the photos «Требует ретуши»; «Открыть» leads to the section of the project they were added in — dropped
   *  with `open: false`, when he adds from that section itself. */
  async queue(ids: readonly string[], { open = true }: { open?: boolean } = {}): Promise<void> {
    const projectId = this.facade.currentProjectId();
    if (!projectId || ids.length === 0) return;
    try {
      await this.activity.markNeedsRetouch(ids);
    } catch {
      this.toast.error(ADD_FAILED);
      return;
    }

    if (!open) {
      this.toast.success('Добавлено в пакетную ретушь');
      return;
    }

    this.toast.action(
      'Добавлено в пакетную ретушь',
      'Открыть',
      () => void this.router.navigate(['/projects', projectId, 'retouch']),
      'success',
    );
  }

  /** Takes waiting photos out of the queue; «Вернуть» queues them again. */
  async unqueue(ids: readonly string[]): Promise<void> {
    const projectId = this.facade.currentProjectId();
    if (!projectId || ids.length === 0) return;
    try {
      await this.facade.skipRetouch(ids);
    } catch {
      this.toast.error('Не удалось убрать — попробуйте ещё раз');
      return;
    }

    const failed = (): void => {
      this.toast.error(ADD_FAILED);
    };

    const n = ids.length;
    this.toast.action(
      n === 1 ? 'Фото убрано из пакетной ретуши' : `${n} фото убраны из пакетной ретуши`,
      'Вернуть',
      () => {
        // The facade tags in the open project: after a switch the undo would land on another project's ids.
        if (this.facade.currentProjectId() !== projectId) failed();
        else void this.activity.markNeedsRetouch(ids).catch(failed);
      },
      'success',
    );
  }
}

/** The lightbox act + pill of a cabinet page («Фото», «Ретушь»). */
export function retouchLightboxProviders(): Provider[] {
  return [{ provide: RETOUCH_LIGHTBOX_PORT, useExisting: FotoclassRetouchQueue }];
}
